import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button, Text } from "@pentabd/ui";
import { IconPlus, IconTrash03 } from "@pentabd/icons";
import {
  CancelButton,
  ErrorModal,
  InputTextAreaRow,
  InputTextRow,
  MultiFileRow,
  SelectControllerRow,
  SuccessModal,
} from "@components";

import useActionCreator from "@redux/hooks/useActionCreator";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import { useSettings } from "@hooks/procurements/useSettings";

import { ROUTES } from "@constants/routes";
import { SETTINGS } from "@constants/procurements/settings";

import { getConvertedMetaData } from "@helpers/inventory/metaDataConvert";
import { PackageCreateType } from "@type/procurements/package";
import {
  PROCUREMENT_PACKAGE,
  packageValidation,
} from "@validations/Procurement/package/packageValidation";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

import { getAccess } from "@helpers/permissionController";
import { useUser } from "@hooks/useUser";

const { PROCUREMENT_NATURE, PROCUREMENT_TYPE, PROCUREMENT_METHOD } = SETTINGS;

interface AddNewPackageFormProps {
  appId: string | number;
  packageId: string | number;
  packageData: PackageCreateType | null;
}

export default function AddNewPackageForm({
  appId,
  packageId,
  packageData,
}: AddNewPackageFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const { settings: procurementNature } = useSettings(PROCUREMENT_NATURE);
  const { settings: procurementType } = useSettings(PROCUREMENT_TYPE);
  const { settings: procurementMethod } = useSettings(PROCUREMENT_METHOD);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const {
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteSuccess,
    deleteError,
  } = useCustomSelector((store) => store.procurements.packageState);
  const { packageCreateActionHandler, packageUpdateActionHandler } =
    useActionCreator();

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<PackageCreateType>({
    resolver: yupResolver(packageValidation),
    values: revertMultipleDocumentData(packageData, [
      PROCUREMENT_PACKAGE.DOCUMENTS,
    ]) as PackageCreateType,
  });

  const { fields, append, remove } = useFieldArray({
    name: PROCUREMENT_PACKAGE.PACKAGE_BUDGET_DETAILS,
    control,
  });

  const createSuccessAction = () => {
    navigate(`${ROUTES.PROCUREMENT.ANNUAL_PROCUREMENT_PLAN}`);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onSubmit: SubmitHandler<PackageCreateType> = (data) => {
    const reqData = convertMultipleDocumentData(data, [
      PROCUREMENT_PACKAGE.DOCUMENTS,
    ]);

    if (packageId) {
      packageUpdateActionHandler(packageId as string, {
        ...reqData,
        appId: Number(appId),
      });
    } else {
      packageCreateActionHandler({ ...reqData, appId: Number(appId) });
    }
  };

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case updateSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case deleteSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setIsErrorModalOpen(true);
        break;
      }
      case !!updateError: {
        setIsErrorModalOpen(true);
        break;
      }
      case !!deleteError: {
        setIsErrorModalOpen(true);
        break;
      }
      default:
        break;
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteError,
    deleteSuccess,
  ]);

  return (
    <div>
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={
            !!createError
              ? createError?.response?.data?.message
              : !!updateError
              ? updateError?.response?.data?.message
              : "Something went wrong"
          }
        />
      )}
      {/* modal end */}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="container p-10">
          <div className="d-flex flex-column gap-10 pb-10 border-bottom">
            <InputTextRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.NAME"
              register={{ ...register(PROCUREMENT_PACKAGE.NAME) }}
              error={errors as any}
              required
            />

            <InputTextRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.TOTAL_BUDGET"
              register={{ ...register(PROCUREMENT_PACKAGE.ALLOCATED_BUDGET) }}
              error={errors as any}
              type="number"
              suffix={t(
                "PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.TOTAL_BUDGET.SUFFIX",
              )}
              required
            />

            <InputTextRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.REF_NO"
              register={{ ...register(PROCUREMENT_PACKAGE.PACKAGE_REF_NO) }}
              error={errors as any}
              required
            />

            <InputTextRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.FINANCIAL_CODE"
              register={{ ...register(PROCUREMENT_PACKAGE.FINANCIAL_CODE) }}
              error={errors as any}
              required
            />

            <InputTextRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.APPROVING_AUTHORITY"
              register={{
                ...register(PROCUREMENT_PACKAGE.APPROVING_AUTHORITY),
              }}
              error={errors as any}
              required
            />

            <SelectControllerRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.PROCUREMENT_NATURE"
              control={control}
              name={PROCUREMENT_PACKAGE.PROCUREMENT_NATURE}
              register={{ ...register(PROCUREMENT_PACKAGE.PROCUREMENT_NATURE) }}
              options={getConvertedMetaData(procurementNature)}
              setValue={setValue}
              required
              error={errors as any}
            />

            <SelectControllerRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.PROCUREMENT_TYPE"
              control={control}
              name={PROCUREMENT_PACKAGE.PROCUREMENT_TYPE}
              register={{ ...register(PROCUREMENT_PACKAGE.PROCUREMENT_TYPE) }}
              options={getConvertedMetaData(procurementType)}
              setValue={setValue}
              required
              error={errors as any}
            />

            <InputTextAreaRow
              label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.DESCRIPTION"
              register={{
                ...register(PROCUREMENT_PACKAGE.PACKAGE_DESCRIPTION),
              }}
              error={errors as any}
            />

            <MultiFileRow
              name={PROCUREMENT_PACKAGE.DOCUMENTS}
              control={control}
              setValue={setValue}
              register={register}
              fileDetails={["documentName", "date", "remarks", "memorialNo"]}
            />

            <div className="py-6">
              <Text color="subtitle1" component="h4" weight="bold" size="lg">
                {t("PROCUREMENT_ADD_PACKAGE.FORM.BUDGET_DETAILS_TITLE")}
              </Text>
            </div>

            {fields.map((item, index) => (
              <div key={item.id}>
                <SelectControllerRow
                  label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.PROCUREMENT_METHOD"
                  control={control}
                  name={`packageBudgetDetails.${index}.${PROCUREMENT_PACKAGE.PROCUREMENT_METHOD}`}
                  register={{
                    ...register(
                      `packageBudgetDetails.${index}.${PROCUREMENT_PACKAGE.PROCUREMENT_METHOD}`,
                    ),
                  }}
                  options={getConvertedMetaData(procurementMethod)}
                  setValue={setValue}
                  required
                  error={errors as any}
                />

                <InputTextRow
                  label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.PROCUREMENT_METHOD_NAME"
                  register={{
                    ...register(
                      `packageBudgetDetails.${index}.${PROCUREMENT_PACKAGE.PACKAGE_BUDGET_TITLE}`,
                    ),
                  }}
                  error={errors as any}
                  required
                />

                <InputTextRow
                  label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.FIRST_QUARTER_BUDGET"
                  register={{
                    ...register(
                      `packageBudgetDetails.${index}.${PROCUREMENT_PACKAGE.FIRST_QUARTER_BUDGET}`,
                    ),
                  }}
                  error={errors as any}
                  required
                  type="number"
                  suffix={t(
                    "PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.TOTAL_BUDGET.SUFFIX",
                  )}
                />

                <InputTextRow
                  label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.SECOND_QUARTER_BUDGET"
                  register={{
                    ...register(
                      `packageBudgetDetails.${index}.${PROCUREMENT_PACKAGE.SECOND_QUARTER_BUDGET}`,
                    ),
                  }}
                  error={errors as any}
                  required
                  type="number"
                  suffix={t(
                    "PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.TOTAL_BUDGET.SUFFIX",
                  )}
                />

                <InputTextRow
                  label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.THIRD_QUARTER_BUDGET"
                  register={{
                    ...register(
                      `packageBudgetDetails.${index}.${PROCUREMENT_PACKAGE.THIRD_QUARTER_BUDGET}`,
                    ),
                  }}
                  error={errors as any}
                  required
                  type="number"
                  suffix={t(
                    "PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.TOTAL_BUDGET.SUFFIX",
                  )}
                />

                <InputTextRow
                  label="PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.FOURTH_QUARTER_BUDGET"
                  register={{
                    ...register(
                      `packageBudgetDetails.${index}.${PROCUREMENT_PACKAGE.FOURTH_QUARTER_BUDGET}`,
                    ),
                  }}
                  error={errors as any}
                  required
                  type="number"
                  suffix={t(
                    "PROCUREMENT_ADD_PACKAGE.FORM.FIELDS.TOTAL_BUDGET.SUFFIX",
                  )}
                />

                <div className="col-md-12 col-lg-10 d-flex justify-content-end mt-10">
                  {getAccess(roles, "PROCUREMENT_HR") && (
                    <Button
                      htmlType="button"
                      type="danger"
                      fill="outline"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      <IconTrash03 size="20" fill="danger" />
                      {t(
                        "PROCUREMENT_ADD_PACKAGE.FORM.DELETE_BUTTON.BUTTON_TEXT",
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {getAccess(roles, "PROCUREMENT_HR") && (
              <Button
                htmlType="button"
                type="light"
                fill="outline"
                onClick={() =>
                  append({
                    [PROCUREMENT_PACKAGE.PROCUREMENT_METHOD]: "",
                    [PROCUREMENT_PACKAGE.PACKAGE_BUDGET_TITLE]: "",
                    [PROCUREMENT_PACKAGE.FIRST_QUARTER_BUDGET]: "",
                    [PROCUREMENT_PACKAGE.SECOND_QUARTER_BUDGET]: "",
                    [PROCUREMENT_PACKAGE.THIRD_QUARTER_BUDGET]: "",
                    [PROCUREMENT_PACKAGE.FOURTH_QUARTER_BUDGET]: "",
                  })
                }
                size="sm"
                className="w-max"
              >
                <IconPlus size="20" fill="dark" />
                {t("PROCUREMENT_ADD_PACKAGE.FORM.ADD_BUTTON.BUTTON_TEXT")}
              </Button>
            )}

            {errors?.[PROCUREMENT_PACKAGE.PACKAGE_BUDGET_DETAILS] && (
              <Text size="sm" color="danger">
                {errors?.[
                  PROCUREMENT_PACKAGE.PACKAGE_BUDGET_DETAILS
                ]?.message?.toString()}
              </Text>
            )}
          </div>

          <div className="col-md-12 col-lg-10 d-flex justify-content-end mt-10 gap-8">
            <CancelButton />

            {getAccess(roles, "PROCUREMENT_HR") && (
              <Button fill="fill" htmlType="submit" type="primary" size="sm">
                {t("PROCUREMENT_ADD_PACKAGE.FORM.SAVE_BUTTON.BUTTON_TEXT")}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
